<script setup>
import { ref } from 'vue'
import { router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'

defineOptions({ layout: AppLayout })
const { t } = useI18n()

const props = defineProps({
  pending:  { type: Array, default: () => [] },
  rejected: { type: Array, default: () => [] }
})

const rejectingId = ref(null)
const rejectReason = ref('')

function approve(v) {
  router.patch(`/admin/approvals/${v.id}/approve`, {}, { preserveScroll: true })
}
function openReject(v) {
  rejectingId.value = v.id
  rejectReason.value = ''
}
function submitReject(v) {
  router.patch(`/admin/approvals/${v.id}/reject`, { reason: rejectReason.value }, {
    preserveScroll: true,
    onSuccess: () => { rejectingId.value = null }
  })
}
</script>

<template>
  <PageShell :title="t('approvals.title')">
    <template #subtitle>
      <p class="text-sm text-[color:var(--inkSoft)]">{{ t('approvals.subtitle') }}</p>
    </template>

    <section class="space-y-3">
      <h2 class="section-title">{{ t('approvals.pendingHeading', { n: pending.length }) }}</h2>

      <p v-if="pending.length === 0" class="empty">{{ t('approvals.emptyPending') }}</p>

      <div v-for="v in pending" :key="v.id" class="card">
        <div class="card__main">
          <img v-if="v.photo_url" :src="v.photo_url" :alt="`${v.make} ${v.model}`" class="thumb" />
          <div v-else class="thumb thumb--blank">{{ v.make?.[0] }}{{ v.model?.[0] }}</div>

          <div class="info">
            <div class="info__title">
              <a :href="`/vehicles/${v.id}`" class="info__link">{{ v.make }} {{ v.model }}<span v-if="v.year" class="info__year"> ({{ v.year }})</span></a>
              <span v-if="v.registration" class="info__reg">· {{ v.registration }}</span>
            </div>
            <div v-if="v.supplier" class="info__supplier">
              {{ t('approvals.from', { name: v.supplier.name }) }}
            </div>
            <div v-if="v.sub_rental" class="info__terms">
              {{ t('approvals.terms', { rate: v.sub_rental.rate, unit: v.sub_rental.unit, deposit: v.sub_rental.deposit }) }}
            </div>
          </div>

          <div class="actions">
            <UiButton size="sm" variant="primary" @click="approve(v)">{{ t('approvals.approve') }}</UiButton>
            <UiButton size="sm" variant="ghost" @click="openReject(v)">{{ t('approvals.reject') }}</UiButton>
          </div>
        </div>

        <div v-if="rejectingId === v.id" class="reject-form">
          <input
            v-model="rejectReason"
            type="text"
            :placeholder="t('approvals.reasonPlaceholder')"
            class="reject-input"
          />
          <UiButton size="sm" variant="danger" @click="submitReject(v)">{{ t('approvals.confirmReject') }}</UiButton>
          <UiButton size="sm" variant="ghost"  @click="rejectingId = null">{{ t('common.cancel') }}</UiButton>
        </div>
      </div>
    </section>

    <section v-if="rejected.length" class="space-y-3 mt-8">
      <h2 class="section-title">{{ t('approvals.rejectedHeading', { n: rejected.length }) }}</h2>
      <p class="rejected-note">{{ t('approvals.rejectedNote') }}</p>
      <div v-for="v in rejected" :key="v.id" class="card card--rejected">
        <div class="card__main">
          <img v-if="v.photo_url" :src="v.photo_url" :alt="`${v.make} ${v.model}`" class="thumb" />
          <div v-else class="thumb thumb--blank">{{ v.make?.[0] }}{{ v.model?.[0] }}</div>
          <div class="info">
            <div class="info__title">
              <a :href="`/vehicles/${v.id}`" class="info__link">{{ v.make }} {{ v.model }}</a>
            </div>
            <div v-if="v.rejection_reason" class="info__reason">{{ v.rejection_reason }}</div>
          </div>
        </div>
      </div>
    </section>
  </PageShell>
</template>

<style scoped>
.section-title {
  font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--muted); margin: 0;
}
.empty {
  padding: 1.5rem; text-align: center; color: var(--muted);
  border: 1px dashed var(--line); border-radius: var(--radius-md, 8px);
}
.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-md, 8px);
  padding: 0.85rem 1rem;
}
.card--rejected { opacity: 0.85; border-style: dashed; }
.card__main { display: flex; gap: 1rem; align-items: center; }
.thumb {
  width: 4.5rem; height: 3.5rem; object-fit: cover;
  border-radius: 0.5rem; background: var(--surfaceAlt);
  flex-shrink: 0;
}
.thumb--blank {
  display: grid; place-items: center;
  font-weight: 700; color: var(--muted);
  font-family: var(--font-display, var(--display));
}
.info { flex: 1; min-width: 0; }
.info__title { font-weight: 600; color: var(--ink); }
.info__link { color: var(--ink); text-decoration: none; }
.info__link:hover { color: var(--accent); }
.info__year { color: var(--muted); font-weight: 400; }
.info__reg  { color: var(--muted); font-weight: 400; }
.info__supplier, .info__terms { font-size: 0.8125rem; color: var(--inkSoft); }
.info__reason { font-size: 0.8125rem; color: var(--danger); }
.actions { display: flex; gap: 0.4rem; flex-shrink: 0; }
.rejected-note { font-size: 0.8125rem; color: var(--muted); margin: 0 0 0.25rem; }

.reject-form {
  display: flex; gap: 0.4rem; margin-top: 0.6rem;
}
.reject-input {
  flex: 1; padding: 0.4rem 0.6rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-input, 6px); color: var(--ink); font-size: 0.875rem;
}
.reject-input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
</style>
